import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/pro-light-svg-icons/faLink';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons/faCircleInfo';
import { TypedMemo } from '../../common/types';
import { Panel, PanelHead } from '../../music24/components/primitives/Panel';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { ErrorAlert } from '../../common/views/ErrorAlert/ErrorAlert';
import { ConnectionInsight, ServiceConnection } from '../../../redux/api/fymAPI.types';
import { ErrorAPIResult } from '../../../redux/api/utils/isSuccessfulResult';
import { ConnectionsDeck } from './ConnectionsDeck';
import { useConnectionsPanelSummary } from './useConnectionsPanelSummary';
import { earlyAccessMessages } from './earlyAccess/earlyAccessMessages';
/* eslint-disable css-modules/no-unused-class -- shared stylesheets; each part uses its own slice of them */
import styles from './ConnectionsPanel.module.scss';
import earlyAccessStyles from './earlyAccess/EarlyAccess.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface ConnectionsPanelProps {
  connections?: ServiceConnection[];
  /** Follower numbers by service. A service missing from it draws the plain card. */
  insights: Record<string, ConnectionInsight>;
  isLoading: boolean;
  error?: ErrorAPIResult['error'];
  retry: () => void;
  onEditProfileUrl: (integrationType: string) => void;
  onConnect: (integrationType: string) => void;
  onSync: (integrationType: string) => void;
  onDisconnect: (integrationType: string) => void;
  /** Opens the artist picker of a login that runs in the app (Ditto Music): "Choose artist", "Change artist". */
  onChooseArtist?: (integrationType: string) => void;
  onShowMatrix: () => void;
  /** True while a portal write is in flight. */
  isBusy: boolean;
  /** Fixed by the screenshot build so two runs draw the same cards. */
  now?: number;
  /** A Sync now or a Disconnect that the server refused. */
  portalError?: ErrorAPIResult['error'];
  /**
   * `canUseAutoUpload`: the Spotify for Artists card shows the Spotify Canvas switch (design
   * spotify-canvas b5).
   */
  showCanvasSwitch?: boolean;
}

/**
 * The service strip. It sits below the catalog (owner decision 2026-10-03):
 * the catalog is what an artist opens the screen for, and the connections
 * matter mostly while they are set up. It never gates anything: an artist who
 * connects nothing still has a full catalog and a full creation flow.
 */
const ConnectionsPanelRendererComponent: React.FC<ConnectionsPanelProps> = ({
  connections,
  insights,
  isLoading,
  error,
  retry,
  onEditProfileUrl,
  onConnect,
  onSync,
  onDisconnect,
  onChooseArtist,
  onShowMatrix,
  isBusy,
  now,
  portalError,
  showCanvasSwitch = false,
}) => {
  const intl = useIntl();

  const { meta, streamingConnections, distributorConnections } = useConnectionsPanelSummary(connections);
  const distributorsSubtitle = intl.formatMessage(earlyAccessMessages.sectionDistributorsSub);

  return (
    <Panel>
      <PanelHead
        icon={<FontAwesomeIcon icon={faLink} />}
        title={intl.formatMessage({
          id: 'app.sml.catalog.connections.title',
          defaultMessage: 'Service connections',
        })}
        right={meta}
      />
      {error !== undefined ? (
        <ErrorView error={error} retry={retry} />
      ) : (
        <>
          {portalError !== undefined && <ErrorAlert error={portalError} />}
          {isLoading || connections === undefined ? (
            <LoadingView />
          ) : (
            <>
              <ConnectionsDeck
                title={intl.formatMessage(earlyAccessMessages.sectionStreaming)}
                subtitle={intl.formatMessage(earlyAccessMessages.sectionStreamingSignIn)}
                connections={streamingConnections}
                showCanvasSwitch={showCanvasSwitch}
                insights={insights}
                onEditProfileUrl={onEditProfileUrl}
                onConnect={onConnect}
                onSync={onSync}
                onDisconnect={onDisconnect}
                onChooseArtist={onChooseArtist}
                isBusy={isBusy}
                now={now}
              />
              {distributorConnections.length > 0 && (
                <>
                  <div className={earlyAccessStyles.sectionRule} />
                  <ConnectionsDeck
                    title={intl.formatMessage(earlyAccessMessages.sectionDistributors)}
                    subtitle={distributorsSubtitle}
                    connections={distributorConnections}
                    insights={insights}
                    onEditProfileUrl={onEditProfileUrl}
                    onConnect={onConnect}
                    onSync={onSync}
                    onDisconnect={onDisconnect}
                    onChooseArtist={onChooseArtist}
                    isBusy={isBusy}
                    now={now}
                  />
                </>
              )}
            </>
          )}
          <div className={styles.connectionSummary}>
            <FontAwesomeIcon icon={faCircleInfo} />
            <p>
              {intl.formatMessage({
                id: 'app.sml.catalog.connections.summary',
                defaultMessage:
                  'You do not need any of these to build smartlinks. A connection adds confirmed ownership, real listener numbers and pre-save delivery on that service.',
              })}
            </p>
            <SMLButton onClick={onShowMatrix} className={styles.smallButton}>
              {intl.formatMessage({
                id: 'app.sml.catalog.connections.matrix',
                defaultMessage: 'What each one unlocks',
              })}
            </SMLButton>
          </div>
        </>
      )}
    </Panel>
  );
};

export const ConnectionsPanelRenderer = TypedMemo(ConnectionsPanelRendererComponent);
